import { type JSX } from 'react'
import { Network } from 'lucide-react'

interface OrchestratorGraphMember {
  id: string
  agentId: string
  name: string
  slug: string
  type: string
  isEnabled: boolean
}

interface OrchestratorGraphSide {
  id: string
  name: string
  slug: string
  description: string | null
  type: string
  isEnabled: boolean
  members: OrchestratorGraphMember[]
}

interface OrchestratorGraphProps {
  main: { name: string; description: string | null; isEnabled: boolean }
  sides: OrchestratorGraphSide[]
}

const treeCss = `
  .org-tree ul {
    position: relative;
    display: flex;
    justify-content: center;
    padding-top: 1.5rem;
    list-style: none;
  }
  .org-tree ul:first-child { padding-top: 0; }
  .org-tree li {
    position: relative;
    float: left;
    padding: 1.5rem 0.5rem 0 0.5rem;
    text-align: center;
  }
  .org-tree li::before, .org-tree li::after {
    content: '';
    position: absolute;
    top: 0;
    right: 50%;
    width: 50%;
    height: 1.5rem;
    border-top: 1px solid #d1d5db;
  }
  .org-tree li::after {
    right: auto;
    left: 50%;
    border-left: 1px solid #d1d5db;
  }
  .org-tree li:only-child::before,
  .org-tree li:only-child::after {
    display: none;
  }
  .org-tree li:only-child { padding-top: 0; }
  .org-tree li:first-child::before,
  .org-tree li:last-child::after {
    border: 0 none;
  }
  .org-tree li:last-child::before {
    border-right: 1px solid #d1d5db;
    border-radius: 0 0.375rem 0 0;
  }
  .org-tree li:first-child::after {
    border-radius: 0.375rem 0 0 0;
  }
  .org-tree ul ul::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 0;
    height: 1.5rem;
    border-left: 1px solid #d1d5db;
  }
`

function NodeCard({
  title,
  subtitle,
  isMain = false,
  isEnabled = true,
}: {
  title: string
  subtitle: string | null
  isMain?: boolean
  isEnabled?: boolean
}): JSX.Element {
  return (
    <div
      className={`inline-flex w-44 flex-col items-center justify-center gap-1 rounded-sm border p-4 shadow-sm ${
        isMain
          ? 'border-blue-200 bg-blue-50'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900">
        <Network className="h-3.5 w-3.5 text-[#3454cf]" />
        <span className={isEnabled ? '' : 'text-gray-400'}>{title}</span>
      </div>
      {subtitle && <span className="line-clamp-2 text-[10px] text-gray-500">{subtitle}</span>}
      {!isEnabled && <span className="text-[10px] text-red-500">Disabled</span>}
    </div>
  )
}

export function OrchestratorGraph({ main, sides }: OrchestratorGraphProps): JSX.Element {
  return (
    <div className="org-tree flex min-h-[24rem] w-full items-start justify-center overflow-auto py-8">
      <style>{treeCss}</style>
      <ul>
        <li>
          <NodeCard title={main.name} subtitle={main.description} isMain isEnabled={main.isEnabled} />
          {sides.length > 0 && (
            <ul>
              {sides.map((side) => (
                <li key={side.id}>
                  <NodeCard title={side.name} subtitle={side.description} isEnabled={side.isEnabled} />
                  {side.members.length > 0 && (
                    <ul>
                      {side.members.map((member) => (
                        <li key={member.id}>
                          <NodeCard title={member.name} subtitle={member.slug} isEnabled={member.isEnabled} />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      </ul>
    </div>
  )
}
